/**
 * ContentObserver — finds content blocks added after load (React-rendered
 * discussions, expanding modules, …). Opt-in via config.observeContent.
 *
 * Cheap by design: mutation callbacks only collect added element nodes; the
 * (debounced) scan only looks inside those nodes, never the whole document.
 */

const WIDGET_UI = '.rv-toolbar, .rv-tb-dropdown, .rv-toggle-btn, .rv-context-menu, .rv-player, .rv-word-highlight, .rv-lms-notice, .rv-simplify-overlay, .rv-magnifier-panel';

export class ContentObserver {
  private observer: MutationObserver | null = null;
  private pending: Set<Element> = new Set();
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    /** Find matching blocks within an added node (including the node itself) */
    private findBlocks: (root: Element) => Element[],
    /** Called with candidate blocks found in a debounced batch */
    private onBlocks: (blocks: Element[]) => void,
    private debounceMs: number = 500
  ) {}

  start(): void {
    if (this.observer || typeof MutationObserver === 'undefined' || !document.body) return;
    this.observer = new MutationObserver((records) => {
      for (const record of records) {
        record.addedNodes.forEach((node) => {
          if (node.nodeType === 1) this.pending.add(node as Element);
        });
      }
      if (this.pending.size && !this.timer) {
        this.timer = setTimeout(() => this.flush(), this.debounceMs);
      }
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  private flush(): void {
    this.timer = null;
    const nodes = Array.from(this.pending);
    this.pending.clear();
    const blocks = new Set<Element>();
    for (const node of nodes) {
      if (!node.isConnected) continue;
      // Our own UI (and nodes inside a node already queued) — skip
      try { if (node.closest(WIDGET_UI)) continue; } catch { /* ignore */ }
      for (const block of this.findBlocks(node)) blocks.add(block);
    }
    if (blocks.size) {
      try { this.onBlocks(Array.from(blocks)); } catch (e) { console.warn('[ReadingVox] content update failed:', e); }
    }
  }

  stop(): void {
    this.observer?.disconnect();
    this.observer = null;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.pending.clear();
  }
}
